import React from 'react';

import { Checkbox, Divider, Popover } from '@mui/material';

import Chip from 'components/Chip';

import { ScheduleStatus, TaskStatus } from 'utils/api/modules/tasks/tasks.constants';

import FilterButton from '../FilterButton';

import { AllOptionText, OptionRow, OptionsContainer } from './styles';
import { STATUS_OPTIONS } from './constants';

export interface StatusOption<T extends string = TaskStatus | ScheduleStatus> {
  value: T;
  label: string;
  colors: {
    background: string;
    foreground: string;
  };
}

const StatusPicker = <T extends string = TaskStatus | ScheduleStatus>({
  value,
  onChange,
  options = STATUS_OPTIONS as StatusOption<T>[],
  testId,
}: {
  value: T[];
  onChange: (value: T[]) => void;
  options?: StatusOption<T>[];
  testId?: string;
}): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const isAllSelected = value.length === 0;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAllClick = (): void => {
    onChange([]);
  };

  const handleOptionClick = (optionValue: T): void => {
    if (isAllSelected) {
      onChange(options.filter((opt) => opt.value !== optionValue).map((opt) => opt.value));
    } else if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const getFirstSelectedOption = (): StatusOption<T> | undefined => {
    if (value.length === 0) return undefined;
    return options.find((opt) => opt.value === value[0]);
  };

  const firstSelected = getFirstSelectedOption();
  const additionalCount = value.length > 1 ? value.length - 1 : 0;

  return (
    <>
      <FilterButton
        prefix="Status"
        value={isAllSelected ? 'All' : undefined}
        onClick={handleOpen}
        isOpen={open}
        testId={testId}
      >
        {firstSelected && (
          <Chip label={firstSelected.label} size="small" colors={firstSelected.colors} capitalizeLabel={false} />
        )}
        {additionalCount > 0 && (
          <Chip
            label={`${additionalCount}+`}
            size="small"
            colors={{ background: 'rgba(0, 0, 0, 0.08)', foreground: 'rgba(0, 0, 0, 0.6)' }}
          />
        )}
      </FilterButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <OptionsContainer>
          <OptionRow onClick={handleAllClick}>
            <AllOptionText>All</AllOptionText>
            <Checkbox checked={isAllSelected} size="small" sx={{ padding: 0 }} />
          </OptionRow>

          <Divider />

          {options.map((option) => {
            const isSelected = isAllSelected || value.includes(option.value);

            return (
              <OptionRow key={option.value} onClick={() => handleOptionClick(option.value)}>
                <Chip label={option.label} size="small" colors={option.colors} capitalizeLabel={false} />
                <Checkbox checked={isSelected} size="small" sx={{ padding: 0 }} />
              </OptionRow>
            );
          })}
        </OptionsContainer>
      </Popover>
    </>
  );
};

export default StatusPicker;
